/**
 * ★ LES CAPTURES DE LA VITRINE S'OUVRENT EN GRAND — demande du 22/08.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * POURQUOI CE FICHIER, ET PAS UNE LIGNE DE PLUS DANS `elements.tsx`
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Une capture de la vitrine s'affiche à 500 px de large sur une page qui en
 * montre quinze. Un directeur qui veut LIRE le total à encaisser, la ligne
 * « à replacer » ou la référence de la QR-facture n'y arrive pas — et c'est
 * exactement ce que la page prétend lui prouver. Le geste attendu est celui de
 * tout le monde : on clique sur l'image, elle s'ouvre en grand.
 *
 * Le comportement est le même pour les quinze visuels. Il vit donc dans UN
 * composant, qui remplace la balise `<img>` dans `Capture` comme dans
 * `CaptureAnnotee` : deux implémentations auraient divergé au premier réglage.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * ★★ CE QUI N'EST PAS RÉÉCRIT ICI — ET C'EST LE POINT IMPORTANT
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Le produit a déjà une primitive de dialogue, `ui/Modal.tsx`, bâtie sur
 * l'élément natif `<dialog>`. Elle apporte, sans une ligne de plus :
 *
 *  · le **piège de focus** et l'inertie de l'arrière-plan (`showModal`) ;
 *  · la fermeture à la touche **Échap** (`cancel`, que la modale intercepte
 *    pour garder l'état React en phase) ;
 *  · la fermeture **au clic hors du cadre** (le clic sur le fond a le
 *    `<dialog>` lui-même pour cible) ;
 *  · un **bouton de fermeture visible**, étiqueté « Fermer », avec sa cible
 *    tactile de 44 px ;
 *  · le **fond qui ne défile pas** pendant l'ouverture.
 *
 * Une seconde primitive de dialogue aurait redémontré tout cela, moins bien.
 * Ce composant n'ajoute donc que les trois choses que la modale ne peut pas
 * connaître :
 *
 *  1. **le déclencheur est un vrai `<button>`.** Une `<img onClick>` n'est pas
 *     atteignable au clavier ; ici la tabulation l'atteint et Entrée comme
 *     Espace l'activent, gratuitement, parce que c'est un bouton. Son nom
 *     accessible est la description de l'image, suivie de « voir en grand » :
 *     qui écoute la page apprend l'argument PUIS ce que l'activation fait ;
 *  2. **le focus REVIENT sur l'image d'origine** à la fermeture. Les
 *     navigateurs le font pour `showModal`, mais seulement quand le
 *     `<dialog>` reste dans le document : ici la modale est démontée à la
 *     fermeture (voir plus bas), donc on rend le focus nous-mêmes, après le
 *     commit qui la retire ;
 *  3. **l'image agrandie tient dans l'écran ET occupe la place qu'elle lui
 *     laisse.** Une hauteur plafonnée en `vh` seule (l'ancien réglage,
 *     `max-h-[70vh]`) coupait l'image bien avant la largeur de la modale —
 *     784 px utilisés sur 1129 px disponibles pour un fichier natif de
 *     1184 px, alors que LIRE LE DÉTAIL était l'objet même de la demande.
 *     La modale reçoit maintenant une hauteur EXPLICITE (`h-[85vh]`, la
 *     même valeur que le plafond déjà posé par `ui/Modal.tsx`) : sans elle,
 *     un `<dialog>` ne se dimensionne que sur son contenu, et la zone de
 *     l'image (`flex-1` dans la modale) n'a rien à distribuer. L'image
 *     remplit alors ce cadre (`h-full w-full object-contain`) et ne peut
 *     grandir au-delà de sa résolution NATIVE (bornée en style, dans les
 *     vrais pixels du fichier) — jamais floue, jamais débordante, ni sur
 *     téléphone ni sur un grand écran.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * ★ LA MODALE N'EXISTE QUE QUAND ELLE EST OUVERTE — et c'est mesuré
 * ────────────────────────────────────────────────────────────────────────────
 *
 * `<Modal>` rend toujours son `<dialog>`, ouvert ou non. Monté d'office, ce
 * seraient quinze dialogues et quinze secondes balises `<img>` dans le HTML
 * d'une page publique qui n'appelle aucune API et dont la légèreté est un
 * argument de vente. La modale n'est donc montée qu'à l'ouverture : la page au
 * repos ne porte rien de plus qu'avant, et le second téléchargement de l'image
 * n'a pas lieu (le navigateur sert la même URL depuis son cache).
 */

import { useEffect, useRef, useState } from 'react'

import { cn } from '../../lib/cn'
import { useLocaliserVitrine } from './localiser'
import { traduireTexteVitrine } from './localiser'
import { useLangueVitrine } from '../../lib/langue'
import { Modal } from '../../ui/Modal'
import type { Visuel } from './visuels'

export interface ImageAgrandissableProps {
  readonly visuel: Visuel
  /** `true` pour la seule capture du premier écran : elle se charge tout de suite. */
  readonly prioritaire?: boolean | undefined
  /** Classes posées sur la balise `<img>` réduite, dans le fil de la page. */
  readonly classeImage?: string | undefined
}

export function ImageAgrandissable({
  visuel,
  prioritaire = false,
  classeImage,
}: ImageAgrandissableProps) {
  const localiser = useLocaliserVitrine()
  const langue = useLangueVitrine()
  const [ouverte, setOuverte] = useState(false)
  const declencheur = useRef<HTMLButtonElement>(null)
  /* Vrai seulement après une VRAIE ouverture : sans ce drapeau, le premier
     rendu (fermé) volerait le focus de la page au chargement. */
  const aEteOuverte = useRef(false)

  useEffect(() => {
    if (ouverte) {
      aEteOuverte.current = true
      return
    }
    if (!aEteOuverte.current) return
    aEteOuverte.current = false
    /* Cet effet tourne APRÈS le commit qui retire le `<dialog>` du document :
       l'arrière-plan n'est plus inerte, le focus peut donc y revenir. */
    declencheur.current?.focus()
  }, [ouverte])

  return localiser(
    <>
      <button
        ref={declencheur}
        type="button"
        onClick={() => {
          setOuverte(true)
        }}
        className={cn(
          'group relative block w-full cursor-zoom-in appearance-none border-0 bg-transparent p-0 text-left',
          /* L'anneau de focus est la moitié de l'exigence « atteignable au
             clavier » : atteindre sans voir où l'on est ne sert à rien. */
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rideau-700',
        )}
      >
        <img
          src={visuel.src}
          width={visuel.largeur}
          height={visuel.hauteur}
          alt={visuel.alt}
          loading={prioritaire ? 'eager' : 'lazy'}
          decoding={prioritaire ? 'sync' : 'async'}
          fetchPriority={prioritaire ? 'high' : 'auto'}
          className={cn('block h-auto w-full bg-slate-100', classeImage)}
        />

        {/* L'affordance visuelle : rien ne dit qu'une image se clique. La
            pastille est décorative — son message est déjà dans le nom
            accessible du bouton, et l'entendre deux fois n'apprend rien. */}
        <span
          aria-hidden="true"
          className="absolute top-2 right-2 flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 text-xs font-medium text-slate-700 opacity-0 shadow-sm transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            className="size-3.5"
          >
            <circle cx="8.5" cy="8.5" r="5.5" />
            <path d="m12.5 12.5 4 4M8.5 6.5v4M6.5 8.5h4" strokeLinecap="round" />
          </svg>
          Agrandir
        </span>

        {/* ⚠️ Retouche du lot A (texte de la vitrine), 22/08 : ce libellé
            portait un tiret cadratin, que le client a écarté sur toute la
            vitrine. Un test le vérifie désormais sur le texte rendu
            (`vitrine.test.tsx`, « la typographie de la vitrine »). Le sens et
            le nom accessible du bouton sont inchangés. */}
        <span className="sr-only">, voir en grand</span>
      </button>

      {ouverte && (
        <Modal
          ouvert
          onFermer={() => {
            setOuverte(false)
          }}
          titre="Vue agrandie"
          libelleFermer={traduireTexteVitrine('Fermer', langue)}
          /* La légende suit l'image dans la modale : c'est elle qui dit ce
             qu'on est censé y lire. Sans elle, l'agrandissement rend le
             détail et perd l'argument. */
          description={visuel.legende}
          taille="xl"
          /* ★ POINT 6 — une hauteur EXPLICITE, pas seulement le plafond
             `max-h-[85vh]` que `ui/Modal.tsx` pose déjà. Un `<dialog>` sans
             hauteur propre se dimensionne sur son contenu : la zone de
             l'image (`flex-1`, dans la modale) n'aurait alors rien à
             distribuer, et l'image replafonnerait sur elle-même. */
          className="h-[85vh]"
        >
          <img
            src={visuel.src}
            width={visuel.largeur}
            height={visuel.hauteur}
            alt={visuel.alt}
            /* `h-full w-full` remplissent le cadre que la modale vient de
               libérer ; `object-contain` y centre l'image sans la déformer,
               ni horizontalement ni verticalement. Les bornes en `style`
               sont les VRAIS pixels du fichier : elles n'interviennent que
               pour empêcher l'agrandissement au-delà de la résolution
               native (l'image deviendrait floue) — jamais pour la
               rétrécir, le cadre s'en charge déjà. */
            style={{ maxWidth: visuel.largeur, maxHeight: visuel.hauteur }}
            className="block h-full w-full rounded-md border border-slate-200 object-contain"
          />
        </Modal>
      )}
    </>,
  )
}
